import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useSearchParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CourseSignature } from "@/components/workspace/CourseSignature";
import { AssignmentTimezone } from "@/components/assignments/assignment-workspace-context";
import { LearnerAssignmentDetail } from "@/components/assignments/LearnerAssignmentDetail";
import { ClassroomAssignmentSurface } from "@/components/courses/ClassroomAssignmentSurface";
import { useRecordSheetSelection } from "@/hooks/use-record-sheet-selection";
import { assignmentsApi, type Submission } from "@/lib/api/assignments";
import { courseAssignmentContextHref } from "@/lib/course-assignment-context";
import { ApiError } from "@/lib/api/client";
import type { CourseHomeProjectionV6 } from "@/lib/api/courseHome";

const unavailable = () => new ApiError(404, "This assignment or submission is unavailable in this course.");

/** This is composition, not a picker. Explicit IDs always reach the existing
 * owner; neither a missing row nor a newer queue selection substitutes a record.
 * Native work in the Coursework register is the register's own row (ADR 0035):
 * this sheet holds the learner's record reached from a session, and the
 * Classroom-owned records.
 */
export function CourseAssignmentSheet({ projection }: { projection: CourseHomeProjectionV6 }) {
  const selection = useRecordSheetSelection("assignment");
  const { closeRecord, openDestination } = selection;
  const [params] = useSearchParams();
  const ambiguous = ["assignment", "submission", "owner"].some(key => params.getAll(key).length > 1);
  const submissionId = params.get("submission");
  const owner = params.get("owner");
  // Exact native work in the Coursework register is the register's row: a
  // teacher's always, a learner's when the address names Coursework.
  const courseworkRow = (owner === null || owner === "native")
    && (projection.perspective === "teaching" || params.get("view") === "coursework");
  const selectedId = courseworkRow ? null : selection.selectedId;
  const provider = owner === "classroom";
  const unsupportedOwner = owner !== null && owner !== "native" && !provider;
  const identity = `${projection.course.id}:${owner ?? "native"}:${selectedId}:${submissionId ?? ""}`;
  const [resolvedTitle, setResolvedTitle] = useState<{ identity: string; title: string } | null>(null);
  const onProviderTitle = useCallback((title: string) => setResolvedTitle(current => current?.identity === identity && current.title === title ? current : { identity, title }), [identity]);
  const location = useLocation();
  const opening = useRef<{ element: HTMLElement | null; rowId: string | null } | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [closedNotice, setClosedNotice] = useState<string | null>(null);
  useEffect(() => {
    if (selectedId !== null) setClosedNotice(null);
    setRefusal(null);
  }, [selectedId, submissionId, owner]);
  const rows = projection.coursework.state === "ready" ? projection.coursework.value.items : [];
  const selectedRow = !provider && !unsupportedOwner ? rows.find(row => row.assignment_id === selectedId) : undefined;
  const providerRow = provider && projection.classroom.enabled ? projection.classroom.items.find(row => row.coursework_id === selectedId) : undefined;
  const returnFocus = () => {
    const original = opening.current;
    const rowId = original?.rowId ?? providerRow?.row_id ?? selectedRow?.row_id;
    const row = Array.from(document.querySelectorAll<HTMLElement>("[data-record-id]"))
      .find(element => element.dataset.recordId === rowId);
    const target = original?.element?.isConnected && original.element !== document.body
      ? original.element
      : row?.querySelector<HTMLElement>("a,button") ?? document.getElementById("course-assignment-heading") ?? document.querySelector<HTMLElement>("[data-course-workspace-frame] h1");
    // Radix calls this after the modal releases its focus scope. The canvas's
    // boolean inert has already been removed by the URL transition.
    target?.focus();
    opening.current = null;
  };
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (selectedId !== null) {
      const state = location.state as { assignmentOpeningRow?: string } | null;
      const element = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      opening.current ??= { element: element?.closest("[data-record-id]") ? element : null, rowId: state?.assignmentOpeningRow ?? providerRow?.row_id ?? selectedRow?.row_id ?? null };
      heading.current?.focus({ preventScroll: true });
      window.scrollTo?.({ top: 0 });
    } else if (opening.current) {
      requestAnimationFrame(returnFocus);
    }
  // Only record identity changes focus. Readback updates must not move it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, owner]);
  if (selectedId === null) return closedNotice ? <p role="status" className="text-ui text-muted-foreground">{closedNotice}</p> : null;
  const title = resolvedTitle?.identity === identity ? resolvedTitle.title : providerRow?.title ?? selectedRow?.title ?? "Assignment";
  const backLabel = params.get("view") === "coursework" ? "Back to Coursework" : params.has("session") ? "Back to session" : "Back to course";
  return <AssignmentTimezone.Provider value={projection.course.timezone}><section aria-label="Assignment workspace" data-assignment-workspace className="mx-auto flex min-w-0 max-w-[90rem] flex-col gap-[var(--layout-bento-gap)] px-4 pb-12 pt-4 sm:px-7 sm:pt-6">
    <header data-slot="page-frame" className="grid min-w-0 gap-2">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex min-w-0 items-center gap-2"><CourseSignature code={projection.course.code} className="size-8 sm:size-8" /><p className="min-w-0 truncate text-ui text-muted-foreground"><span className="font-semibold text-foreground">{projection.course.code}</span><span className="hidden sm:inline"> · {projection.course.title}</span></p></div>
        <Button size="sm" variant="subtle" onClick={() => { setClosedNotice(refusal); closeRecord(); }}><ArrowLeft className="size-4" strokeWidth={1.75} aria-hidden />{backLabel}</Button>
      </div>
      <h1 ref={heading} tabIndex={-1} className="break-words text-title font-semibold outline-none">{title}</h1>
    </header>
    {selectedId !== null && (ambiguous ? <p role="alert" className="text-ui text-muted-foreground">This assignment link is invalid. Open the assignment from Coursework.</p> : unsupportedOwner ? <p role="alert" className="text-ui text-muted-foreground">This assignment link is unsupported. Open the assignment from Coursework.</p> : provider ? <ClassroomAssignmentSurface
      key={`${projection.course.id}:classroom:${selectedId}:${submissionId ?? ""}`}
      courseId={projection.course.id}
      perspective={projection.perspective}
      courseworkId={selectedId}
      submissionId={submissionId}
      timezone={projection.course.timezone}
      onSelectDestination={href => openDestination(courseAssignmentContextHref(href, location.pathname, location.search), true)}
      onTitleChange={onProviderTitle}
    /> : <AssignmentOwner
      key={`${projection.course.id}:${selectedId}:${submissionId ?? ""}`}
      projection={projection}
      assignmentId={selectedId}
      submissionId={submissionId}
      onRefusal={setRefusal}
      onTitleChange={onProviderTitle}
      onSelectReceipt={id => {
        const next = new URLSearchParams(params);
        next.set("submission", id);
        openDestination(`${location.pathname}?${next}${location.hash}`, true);
      }}
    />)}
  </section></AssignmentTimezone.Provider>;
}

/** The learner's own record: the brief, every attempt, and the addressed receipt. */
function AssignmentOwner({ projection, assignmentId, submissionId, onRefusal, onSelectReceipt, onTitleChange }: {
  projection: CourseHomeProjectionV6;
  assignmentId: string;
  submissionId: string | null;
  onRefusal: (message: string | null) => void;
  onTitleChange: (title: string) => void;
  onSelectReceipt: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const courseId = projection.course.id;
  const assignment = useQuery({
    queryKey: ["assignment-sheet", courseId, assignmentId, projection.perspective],
    queryFn: async () => {
      const record = await assignmentsApi.get(assignmentId);
      if (record.id !== assignmentId || record.course_id !== courseId || record.status === "draft") throw unavailable();
      return record;
    },
    retry: false,
  });
  const attempts = useQuery({
    queryKey: ["assignment", assignmentId, "submissions"],
    queryFn: () => assignmentsApi.listSubmissions(assignmentId),
    enabled: Boolean(assignment.data),
    retry: false,
  });
  const receipt = useQuery({
    queryKey: ["assignment-sheet", courseId, assignmentId, "receipt", submissionId],
    enabled: submissionId !== null && Boolean(assignment.data),
    retry: false,
    queryFn: async () => {
      const record = await assignmentsApi.getSubmission(submissionId!);
      if (record.id !== submissionId || record.assignment_id !== assignmentId || record.course_id !== courseId
        || (projection.perspective === "learning" && record.enrollment_id !== projection.relationship.enrollment_id)) throw unavailable();
      return record;
    },
  });
  const refusal = assignment.isError ? "This assignment is unavailable in this course."
    : receipt.isError ? "This submission is unavailable." : null;
  useEffect(() => { onRefusal(refusal); }, [refusal, onRefusal]);
  useEffect(() => { if (assignment.data) onTitleChange(assignment.data.title); }, [assignment.data, onTitleChange]);
  if (assignment.isPending) return <p role="status" className="text-ui text-muted-foreground">Loading this assignment...</p>;
  if (assignment.isError || !assignment.data) return <p role="alert" className="text-ui text-muted-foreground">This assignment is unavailable in this course.</p>;
  if (submissionId !== null) {
    if (receipt.isPending) return <p role="status" className="text-ui text-muted-foreground">Loading submission...</p>;
    if (receipt.isError) return <p role="alert" className="text-ui text-muted-foreground">This submission is unavailable.</p>;
  }
  const record = assignment.data;
  return <LearnerAssignmentDetail
    assignment={record}
    term={{ name: projection.course.term?.name ?? null, status: projection.course.term?.status ?? null }}
    attempts={(attempts.data ?? []).filter(attempt => attempt.course_id === courseId && attempt.assignment_id === assignmentId
      && projection.perspective === "learning" && attempt.enrollment_id === projection.relationship.enrollment_id)}
    receipt={receipt.data}
    onSelectReceipt={onSelectReceipt}
    onSubmitted={(result: Submission) => {
      queryClient.setQueryData(["assignment-sheet", courseId, assignmentId, "receipt", result.id], result);
      onSelectReceipt(result.id);
    }}
    attemptsPending={attempts.isPending}
    attemptsFailed={attempts.isError}
    onRetryAttempts={() => void attempts.refetch()}
  />;
}
